import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import { GestionKpis } from './GestionKpis';
import { GestionDailyChart, GestionResponses } from './GestionCharts';
import { GestionUsersTable } from './GestionUsersTable';

const isoDay = (d) => d.toISOString().slice(0, 10);
const todayIso = () => isoDay(new Date(Date.now() - new Date().getTimezoneOffset() * 60000));

const periodParams = (period, range) => {
  if (period === 'month') { const t = todayIso(); return { date_from: `${t.slice(0, 7)}-01`, date_to: t }; }
  if (period === 'custom') return range.from && range.to ? { date_from: range.from, date_to: range.to } : null;
  return { days: Number(period) };
};

export const GestionMetrics = ({ portfolioId }) => {
  const [period, setPeriod] = useState('30');
  const [range, setRange] = useState({ from: '', to: todayIso() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const params = periodParams(period, range);
    if (!params) return;
    setLoading(true);
    try {
      const res = await crmService.metrics({ ...params, portfolio_id: portfolioId || undefined });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudieron cargar las métricas de gestión'); } finally { setLoading(false); }
  }, [period, range, portfolioId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-4" data-testid="gestion-metrics">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-teal-600" />Gestión</h3>
          <p className="text-sm text-slate-600">Actividad del equipo, contacto efectivo y promesas de pago{data ? ` · ${data.period.from.split('-').reverse().join('/')} a ${data.period.to.split('-').reverse().join('/')}` : ''}</p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[170px]" data-testid="gestion-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="month">Mes actual</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="custom">Rango de fechas…</SelectItem>
            </SelectContent>
          </Select>
          {period === 'custom' && (
            <>
              <Input type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} className="w-[150px]" data-testid="gestion-range-from" />
              <span className="text-xs text-slate-400">a</span>
              <Input type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-[150px]" data-testid="gestion-range-to" />
            </>
          )}
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      </div>

      {data && (
        <>
          <GestionKpis totals={data.totals} promises={data.promises} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><GestionDailyChart daily={data.daily} /></div>
            <GestionResponses responses={data.responses} />
          </div>
          <GestionUsersTable users={data.users} />
        </>
      )}
    </section>
  );
};

export default GestionMetrics;
